import {
  forwardRef,
  useMemo
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import {
  Icon,
  StringDateTimeFromNow
} from '@febos-io/design';
import { store } from '@febos-io/auth';
import FilePreview
  from '@febos-io/common/src/components/FilePreview/FilePreview';
import useTranslation from '../../hooks/useTranslation';

const extractTextAndImages = (text) => {
  // Regex para encontrar las referencias de imágenes, ejemplo: [image: some-id.jpg]
  const imageRegex = /\[image:.*\.*\]/gm;

  // Usamos matchAll para capturar todas las coincidencias y partes del texto
  let parts = [];
  let lastIndex = 0;

  // Recorremos todas las coincidencias en el texto
  // eslint-disable-next-line no-restricted-syntax
  for (const match of text.matchAll(imageRegex)) {
    // Guardamos el texto anterior a la imagen
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }

    // Guardamos la referencia de la imagen
    parts.push({ type: 'image', content: match[0] });

    // Actualizamos el índice para seguir recorriendo
    lastIndex = match.index + match[0].length;
  }

  // Guardamos el texto restante después de la última imagen, si lo hay
  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return parts;
};

export const ReceivingMessage = forwardRef(function ReceivingMessage({ message, withIcon }, ref) {
  const { t } = useTranslation();
  const userId = useSelector(store.getUserId);
  const isMe = useMemo(() => {
    return message.user.id === userId;
  }, [message.user.id, userId]);
  const messageText = useMemo(
    () => {
      const attachments = message.attachments || [];
      const parts = extractTextAndImages(message.message);
      return parts.map((part, index) => {
        if (part.type === 'image') {
          const imageName = part.content.replace('[image:', '').replace(']', '').trim();
          const attachment = attachments.find((a) => a.name === imageName);
          if (attachment) {
            return (
              <FilePreview
                key={`image-message-${index + 1}`}
                file={attachment}
              />
            );
          }
          return null;
        }
        // Renderizamos el texto normal
        return (
          <div
            key={`message-${index + 1}`}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: part.content }}
          />
        );
      });
    },
    [message.attachments, message.message]
  );
  return (
    <li
      className={classNames('d-flex my-1', {
        'justify-content-start': !isMe,
        'justify-content-end': isMe
      })}
      ref={ref}
    >
      {!isMe && withIcon && (
        <Icon
          icon="message"
          className="d-flex align-self-start me-3 shadow-1-strong text-primary fa-flip-horizontal"
          fontSize="20"
        />
      )}
      <div className="card w-100 border">
        <div className="card-header d-flex justify-content-between border-bottom">
          <strong className="mb-0 me-1">{isMe ? t('Me') : message.user.name}</strong>
          <p className="text-muted small mb-0">
            <Icon icon="clock" className="me-1" />
            <StringDateTimeFromNow value={message.createdAt} />
          </p>
        </div>
        <div className="card-body">
          <div className="mb-0 mt-1">{messageText}</div>
        </div>
        <ul className="list-unstyled">
          {message.attachments?.map((attachment, index) => (
            <li key={`attachment-message-${index + 1}`}>
              <Icon icon="paperclip" />
              <a
                href={attachment.fileKeySigned}
                target="_blank"
                rel="noreferrer"
                download
              >
                {attachment.name}
              </a>
            </li>
          ))}
        </ul>
      </div>
      {isMe && withIcon && (
        <Icon
          icon="message"
          className="d-flex align-self-start me-3 shadow-1-strong text-primary fa-flip-horizontal"
          fontSize="20"
        />
      )}
    </li>
  );
});

ReceivingMessage.propTypes = {
  message: PropTypes.object,
  withIcon: PropTypes.bool
};

export default ReceivingMessage;
